import React, { useEffect, useRef, useState } from 'react'
import * as echarts from 'echarts'
import {
    WalletIcon,
    UserIcon,
    TicketIcon,
    UserGroupIcon,
    UsersIcon,
    UserCircleIcon,
    CheckCircleIcon,
    XCircleIcon,
    CheckBadgeIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/solid'
import { Select } from 'components/select'
import stSeat from '../../assets/images/icons/st_seat.svg'
import vipSeat from '../../assets/images/icons/vip_seat.svg'
import { DATE_RANGES, DateFilter } from './components/DateFilter'
import { useLazyQuery } from '@apollo/client'
import { GET_BOOKINGS_DASHBOARD } from 'graphql/dashboard/bookings'
import { DashboardSkeleton } from './components/DashboardSkeleton'
import { GraphQlResponseNoData } from 'components/custom/GraphQlResponseDisplay'
import i18next from 'i18next'
import { BookingsDashboardSkeleton } from './components/BookingsDashboardSkeleton'
import { useTranslation } from 'react-i18next'
import html2pdf from 'html2pdf.js'
import { PrinterIcon } from '@heroicons/react/24/outline'

const StatCard = ({ title, value, type, icon, iconBg }) => (
    <div className="flex items-center justify-between bg-white p-4 py-2 rounded-lg shadow border border-gray-100">
        <div>
            <h3 className="text-gray-900 text-sm font-semibold">{title}</h3>
            <p className="text-lg font-bold text-primary">{value}</p>
        </div>
        <div className={`${iconBg || 'bg-primary'} py-2 px-2 rounded-xl flex items-center justify-center`}>{icon}</div>
    </div>
)

const ProgressBar = ({ label, percentage, ageRange }) => (
    <div className="mb-4">
        <div className="flex justify-between mb-1">
            <span className="text-gray-800 text-sm font-semibold">{label}</span>
            <span className="text-gray-600 text-md font-bold">{percentage}%</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded">
            <div
                className="h-full rounded"
                style={{
                    width: `${percentage}%`,
                    background: 'linear-gradient(to right, #1f515d, #83b6c3)',
                }}
            ></div>
        </div>
        <span className="text-xs text-gray-500">{ageRange}</span>
    </div>
)

const ROUTE_TYPES = {
    ALL: null,
    DOMESTIC: 'DMC',
    INTERNATIONAL: 'INL',
}

const Dashboard = () => {
    const { t } = useTranslation()

    const StatProgressBar = ({ label, percentage = 0, range, iconBg, iconColor, Icon }) => (
        <div className="flex items-center gap-4 mb-4">
            <div className={`${iconBg} p-2 rounded-lg`}>
                <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div className="flex-1">
                <div className="flex justify-between mb-1">
                    <div>
                        <span className="text-gray-800 font-medium">{label}</span>
                        <span className="text-gray-400 text-sm ml-2">{range}</span>
                    </div>
                    <span className="text-gray-600 text-md font-bold">{percentage}%</span>
                </div>
                <div className="w-full h-[12px] bg-gray-100 rounded-full">
                    <div
                        className="h-full rounded-full"
                        style={{
                            width: `${percentage}%`,
                            background:
                                label === 'Young-Adults' ? '#caa72b' : 'linear-gradient(to right, #5d8e9a, #83b6c3)',
                        }}
                    ></div>
                </div>
            </div>
        </div>
    )

    const RouteProgress = ({ route, value }) => (
        <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
                <span className="text-primary text-sm font-medium">{route}</span>
                <span className="text-gray-600 whitespace-nowrap !text-[16px] font-bold">{value}</span>
            </div>
            <div className="w-full h-[3px] bg-gray-100 rounded-full overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-[#5d8e9a] to-[#83b6c3] rounded-full"
                    style={{ width: `${(value / 120) * 100}%` }}
                ></div>
            </div>
        </div>
    )

    const DEMOGRAPHICS_CONFIG = [
        {
            label: t('Children'),
            key: 'child_percentage',
            range: t('Under-18'),
            iconBg: 'bg-primary/40',
            iconColor: 'text-primary',
            Icon: UserIcon,
        },
        {
            label: t('Young-Adults'),
            key: 'infant_percentage',
            range: '18-30',
            iconBg: 'bg-[#d4b242]/30',
            iconColor: 'text-[#d4b242]',
            Icon: UserCircleIcon,
        },
        {
            label: t('Adults'),
            key: 'adult_percentage',
            range: '31-50',
            iconBg: 'bg-primary/20',
            iconColor: 'text-primary',
            Icon: UsersIcon,
        },
        {
            label: t('Seniors'),
            key: 'senior_percentage',
            range: '51+',
            iconBg: 'bg-primary/5',
            iconColor: 'text-primary/40',
            Icon: UserGroupIcon,
        },
    ]

    const [dashboardData, setDashboardData] = useState(null)
    const [selectedRouteType, setSelectedRouteType] = useState(ROUTE_TYPES.ALL)
    const [currentDateFilter, setCurrentDateFilter] = useState({ type: DATE_RANGES.TODAY, from: null, to: null })
    const chartRef = useRef(null)
    const hourlyChartRef = useRef(null)

    const [getBookingsDashboard, { loading }] = useLazyQuery(GET_BOOKINGS_DASHBOARD, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getBookingsDashboard?.success) {
                setDashboardData(data.getBookingsDashboard.bookings_dashboard)
                updateCharts(data.getBookingsDashboard.bookings_dashboard)
            }
        },
    })

    const handleDateChange = ({ type, from, to }) => {
        setCurrentDateFilter({ type, from, to })
        if (type === 'CUSTOM_RANGE') {
            getBookingsDashboard({
                variables: {
                    dateRange: {
                        start_date: from,
                        end_date: to,
                    },
                    routeType: selectedRouteType,
                },
            })
        } else {
            getBookingsDashboard({
                variables: {
                    period: type,
                    routeType: selectedRouteType,
                },
            })
        }
    }

    const handleRouteTypeChange = (routeType) => {
        setSelectedRouteType(routeType)
        const { type, from, to } = currentDateFilter
        if (type === 'CUSTOM_RANGE') {
            getBookingsDashboard({
                variables: {
                    dateRange: {
                        start_date: from,
                        end_date: to,
                    },
                    routeType: routeType,
                },
            })
        } else {
            getBookingsDashboard({
                variables: {
                    period: type,
                    routeType: routeType,
                },
            })
        }
    }

    const updateCharts = data => {
        // VIP vs Standard Chart
        if (data?.ticket_class_distribution?.length) {
            const initVipStdChart = () => {
                if (!chartRef.current) {
                    setTimeout(initVipStdChart, 100)
                    return
                }
                echarts.dispose(chartRef.current)
                const chart = echarts.init(chartRef.current)
                const option = {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                    },
                    grid: {
                        left: '1%',
                        right: '1%',
                        bottom: '10%',
                        top: '5%',
                        containLabel: true,
                    },
                    xAxis: {
                        type: 'category',
                        data: data.ticket_class_distribution.map(item => item.period_label.split(' ').join('\n')),
                        axisLabel: {
                            color: '#374151',
                            interval: 0,
                            formatter: function (value) {
                                return value.split('\n').join('\n')
                            },
                            lineHeight: 15,
                            align: 'center',
                        },
                        axisLine: {
                            show: true,
                            lineStyle: {
                                color: '#edf2f7',
                            },
                        },
                    },
                    yAxis: [
                        {
                            type: 'value',
                            axisLabel: {
                                color: '#374151',
                                margin: 14,
                            },
                            splitLine: {
                                show: true,
                                lineStyle: {
                                    color: '#edf2f7',
                                },
                            },
                            axisLine: {
                                show: true,
                                lineStyle: {
                                    color: '#edf2f7',
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            name: 'VIP',
                            type: 'bar',
                            data: data.ticket_class_distribution.map(item => item.vip_seat_count),
                            color: '#caa72b',
                            barWidth: '30%',
                            itemStyle: {
                                borderRadius: [3, 3, 0, 0],
                                shadowColor: 'rgba(0, 0, 0, 0.1)',
                                shadowBlur: 5,
                            },
                            emphasis: {
                                itemStyle: {
                                    color: '#d4b242',
                                },
                            },
                        },
                        {
                            name: 'Standard',
                            type: 'bar',
                            data: data.ticket_class_distribution.map(item => item.std_seat_count),
                            color: '#0c505b',
                            barWidth: '30%',
                            itemStyle: {
                                borderRadius: [3, 3, 0, 0],
                                shadowColor: 'rgba(0, 0, 0, 0.1)',
                                shadowBlur: 5,
                            },
                            emphasis: {
                                itemStyle: {
                                    color: '#6fa0ad',
                                },
                            },
                        },
                    ],
                }
                chart.setOption(option)
                window.addEventListener('resize', () => chart.resize())
                return () => chart.dispose()
            }
            initVipStdChart()
        }

        // Hourly Performance Chart
        if (data?.hourly_booking_trends?.length) {
            const initHourlyChart = () => {
                if (!hourlyChartRef.current) {
                    setTimeout(initHourlyChart, 100)
                    return
                }
                echarts.dispose(hourlyChartRef.current)
                const hourlyChart = echarts.init(hourlyChartRef.current)
                const hourlyOption = {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                    },
                    grid: {
                        left: '1%',
                        right: '1%',
                        bottom: '2%',
                        top: '2%',
                        containLabel: true,
                    },
                    xAxis: {
                        type: 'category',
                        data: data.hourly_booking_trends.map(item => item.booking_hour_label.split(' ').join('\n')),
                        axisLabel: {
                            color: '#374151',
                            interval: 0,
                            formatter: function (value) {
                                return value.split('\n').join('\n')
                            },
                            lineHeight: 15,
                            align: 'center',
                        },
                        axisLine: {
                            show: true,
                            lineStyle: {
                                color: '#edf2f7',
                            },
                        },
                    },
                    yAxis: [
                        {
                            type: 'value',
                            axisLabel: {
                                color: '#374151',
                            },
                            splitLine: {
                                show: true,
                                lineStyle: {
                                    color: '#edf2f7',
                                },
                            },
                            axisLine: {
                                show: true,
                                lineStyle: {
                                    color: '#edf2f7',
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            data: data.hourly_booking_trends.map(item => item.booking_count),
                            type: 'bar',
                            barWidth: '60%',
                            itemStyle: {
                                color: '#0c505b',
                                borderRadius: [3, 3, 0, 0],
                                shadowColor: 'rgba(0, 0, 0, 0.1)',
                                shadowBlur: 5,
                            },
                            label: {
                                show: true,
                                position: 'insideBottom',
                                color: '#ffffff',
                                fontSize: 12,
                                rotate: 90,
                                distance: 15,
                                formatter: function (params) {
                                    return `${params.value}`
                                },
                                verticalAlign: 'middle',
                                textBorderColor: '#0c505b',
                                textBorderWidth: 2,
                                textShadowBlur: 2,
                            },
                            emphasis: {
                                itemStyle: {
                                    color: '#6fa0ad',
                                },
                            },
                        },
                    ],
                }
                hourlyChart.setOption(hourlyOption)
                window.addEventListener('resize', () => hourlyChart.resize())
                return () => hourlyChart.dispose()
            }
            initHourlyChart()
        }
    }

    const handleDownloadPDF = () => {
        const element = document.getElementById('dashboard-pdf-content')
        if (!element) return
        // Add force-print class to enable print styles
        element.classList.add('force-print')
        html2pdf()
            .set({
                margin: 0.5,
                filename: 'bookings_dashboard.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
            })
            .from(element)
            .save()
            .then(() => {
                element.classList.remove('force-print')
            })
            .catch(() => {
                element.classList.remove('force-print')
            })
    }

    useEffect(() => {
        getBookingsDashboard({
            variables: {
                period: DATE_RANGES.TODAY,
                routeType: null,
            },
        })
    }, [])

    useEffect(() => {
        if (dashboardData && (chartRef.current || hourlyChartRef.current)) {
            updateCharts(dashboardData)
        }
    }, [dashboardData])

    useEffect(() => {
        if (dashboardData && (chartRef.current || hourlyChartRef.current)) {
            updateCharts(dashboardData)
        }
    }, [i18next.language])

    return (
        <div className="w-full">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6 print:flex-col print:items-stretch print:gap-2 print:mb-2">
                <h1 className="text-2xl font-bold text-gray-800 w-full md:w-auto text-center md:text-left print:text-lg print:font-bold">
                    {t('Booking Overview')}
                </h1>
                <div className="flex gap-2 w-full md:w-auto print:w-full print:gap-1">
                    <div className="w-[150px] print:hidden">
                        <Select
                            value={selectedRouteType || ''}
                            onChange={e => handleRouteTypeChange(e.target.value || null)}
                            className="w-full"
                        >
                            <option value="">{t('All Routes')}</option>
                            <option value={ROUTE_TYPES.DOMESTIC}>{t('Domestic')}</option>
                            <option value={ROUTE_TYPES.INTERNATIONAL}>{t('International')}</option>
                        </Select>
                    </div>
                    <DateFilter onDateChange={handleDateChange} className="w-full md:w-auto print:hidden" />
                    <button
                        onClick={handleDownloadPDF}
                        className="bg-primary text-white px-3 py-1 text-sm rounded-lg shadow hover:bg-primary/80 transition flex items-center gap-2 print:hidden"
                    >
                        <PrinterIcon className="w-4 h-4" />
                        {t('Download PDF')}
                    </button>
                </div>
            </div>
            <div id="dashboard-pdf-content" className="print:bg-white print:p-2">
                {loading ? (
                    <BookingsDashboardSkeleton />
                ) : (
                    <>
                        <div className="mb-8 relative">
                            <div className="bg-white rounded-xl shadow border border-gray-100 p-4 pt-8">
                                <div className="absolute -top-3 left-4 z-10">
                                    <span className="px-3 py-1 text-gray-800 bg-white text-md font-bold">
                                        {t('Booking Overview')}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 print:grid-cols-3 print:gap-2 print:mb-2">
                                    <StatCard
                                        title={t('Total')}
                                        value={dashboardData?.total_bookings || 0}
                                        icon={<TicketIcon className="w-5 h-5 text-primary" />}
                                        iconBg="bg-primary/10"
                                    />
                                    <StatCard
                                        title={t('Confirmed')}
                                        value={dashboardData?.confirmed_bookings || 0}
                                        icon={<CheckBadgeIcon className="w-5 h-5 text-blue/90" />} // Use CheckBadgeIcon
                                        iconBg="bg-blue/10"
                                    />
                                    <StatCard
                                        title={t('Completed')}
                                        value={dashboardData?.completed_bookings || 0}
                                        icon={<CheckCircleIcon className="w-5 h-5 text-green/90" />} // Use CheckCircleIcon
                                        iconBg="bg-green/10"
                                    />
                                    <StatCard
                                        title={t('Active (On-Trip)')}
                                        value={dashboardData?.ontrip_bookings || 0}
                                        icon={<UserGroupIcon className="w-5 h-5 text-lime-900" />}
                                        iconBg="bg-lime-100"
                                    />
                                    <StatCard
                                        title={t('No-Show')}
                                        value={dashboardData?.noshow_bookings || 0}
                                        icon={<UserIcon className="w-5 h-5 text-gray-700" />}
                                        iconBg="bg-gray-100"
                                    />
                                    <StatCard
                                        title={t('Cancelled')}
                                        value={dashboardData?.cancelled_bookings || 0}
                                        icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                        iconBg="bg-[#fae6eb]"
                                    />
                                    <StatCard
                                        title={t('Trip Cancelled')}
                                        value={dashboardData?.trip_cancelled_bookings || 0}
                                        icon={<ExclamationTriangleIcon className="w-5 h-5 text-orange-400" />}
                                        iconBg="bg-orange-100"
                                    />
                                    <StatCard
                                        title={t('Total Passenger')}
                                        value={dashboardData?.total_passengers || 0}
                                        icon={<UsersIcon className="w-5 h-5 text-primary/90" />}
                                        iconBg="bg-primary/10"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mb-8 flex items-center gap-6">
                            <div className="w-full relative">
                                <div className="bg-white rounded-xl w-full shadow border border-gray-100 p-4 pt-8">
                                    <div className="absolute -top-3 left-4 z-10">
                                        <span className="px-3 py-1 text-gray-800 bg-white text-md font-bold">
                                            {t('Total Passenger Traveled (Standared Booking)')}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4 print:grid-cols-2 print:gap-2 print:mb-2">
                                        <StatCard
                                            title={t('Passengers Transported')}
                                            value={dashboardData?.std_passengers_completed || 0}
                                            icon={<img src={stSeat} className="w-5 h-5" alt="Standard" />}
                                            iconBg="bg-primary/10"
                                        />
                                        <StatCard
                                            title={t('Passengers Cancelled')}
                                            value={dashboardData?.std_passengers_cancelled || 0}
                                            icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                            iconBg="bg-[#fae6eb]"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="w-full relative">
                                <div className="bg-white rounded-xl w-full shadow border border-gray-100 p-4 pt-8">
                                    <div className="absolute -top-3 left-4 z-10">
                                        <span className="px-3 py-1 text-gray-800 bg-white text-md font-bold">
                                            {t('Total Passenger Traveled (Daypass Booking)')}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4 print:grid-cols-2 print:gap-2 print:mb-2">
                                        <StatCard
                                            title={t('Passengers Transported')}
                                            value={dashboardData?.daypass_passengers_completed || 0}
                                            icon={<TicketIcon className="w-5 h-5 text-primary" />}
                                            iconBg="bg-primary/10"
                                        />
                                        <StatCard
                                            title={t('Passengers Cancelled')}
                                            value={dashboardData?.daypass_passengers_cancelled || 0}
                                            icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                            iconBg="bg-[#fae6eb]"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-5 flex flex-col lg:flex-row gap-6 print:flex-col print:gap-2">
                            <div className="relative flex-1 w-full lg:w-[40%] min-w-0 print:w-full">
                                <div className="bg-white rounded-xl shadow border border-gray-100 p-4 pt-8 h-full">
                                    <div className="absolute -top-3 left-4 z-10">
                                        <span className="px-3 py-1 text-gray-800 bg-white text-md font-bold">
                                            {t('Standard Booking')}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <StatCard
                                            title={t('Total')}
                                            value={dashboardData?.total_std_bookings || 0}
                                            icon={<img src={stSeat} className="w-5 h-5" alt="Standard" />}
                                            iconBg="bg-primary/10"
                                        />
                                        <StatCard
                                            title={t('Completed')}
                                            value={dashboardData?.completed_std_bookings || 0}
                                            icon={<CheckCircleIcon className="w-5 h-5 text-green/90" />}
                                            iconBg="bg-green/10"
                                        />
                                        <StatCard
                                            title={t('Cancelled')}
                                            value={dashboardData?.cancelled_std_bookings || 0}
                                            icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                            iconBg="bg-[#fae6eb]"
                                        />
                                        <StatCard
                                            title={t('Trip Cancelled')}
                                            value={dashboardData?.trip_cancelled_std_bookings || 0}
                                            icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                            iconBg="bg-[#fae6eb]"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="relative flex-1 w-full lg:w-[40%] min-w-0 print:w-full">
                                <div className="bg-white rounded-xl shadow border border-gray-100 p-4 pt-8 h-full">
                                    <div className="absolute -top-3 left-4 z-10">
                                        <span className="px-3 py-1 text-gray-800 bg-white text-md font-bold">
                                            {t('Day-Pass Booking')}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <StatCard
                                            title={t('Total')}
                                            value={dashboardData?.total_daypass_bookings || 0}
                                            icon={<TicketIcon className="w-5 h-5 text-primary/90" />}
                                            iconBg="bg-primary/10"
                                        />
                                        <StatCard
                                            title={t('Standard')}
                                            value={dashboardData?.total_daypass_std_bookings || 0}
                                            icon={<UserIcon className="w-5 h-5 text-blue/90" />}
                                            iconBg="bg-blue/10"
                                        />
                                        <StatCard
                                            title={t('Completed')}
                                            value={dashboardData?.completed_daypass_std_bookings || 0}
                                            icon={<CheckCircleIcon className="w-5 h-5 text-green/90" />}
                                            iconBg="bg-green/10"
                                        />
                                        <StatCard
                                            title={t('Cancelled')}
                                            value={dashboardData?.cancelled_daypass_std_bookings || 0}
                                            icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                            iconBg="bg-[#fae6eb]"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-8 relative">
                            <div className="bg-white rounded-xl shadow border border-gray-100 p-4 pt-8">
                                <div className="absolute -top-3 left-4 z-10">
                                    <span className="px-3 py-1 text-gray-800 bg-white text-md font-bold">
                                        {t('Charter Boat Booking')}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 print:grid-cols-3 print:gap-2 print:mb-2">
                                    <StatCard
                                        title={t('Total')}
                                        value={dashboardData?.total_chartered_boat_bookings || 0}
                                        icon={<TicketIcon className="w-5 h-5 text-primary" />}
                                        iconBg="bg-primary/10"
                                    />
                                    <StatCard
                                        title={t('Under-Review')}
                                        value={dashboardData?.under_review_chartered_boat_bookings || 0}
                                        icon={<UserGroupIcon className="w-5 h-5 text-lime-900" />}
                                        iconBg="bg-lime-100"
                                    />
                                    <StatCard
                                        title={t('Approved')}
                                        value={dashboardData?.approved_chartered_boat_bookings || 0}
                                        icon={<UserIcon className="w-5 h-5 text-gray-700" />}
                                        iconBg="bg-gray-100"
                                    />
                                    <StatCard
                                        title={t('Confirmed')}
                                        value={dashboardData?.confirmed_chartered_boat_bookings || 0}
                                        icon={<CheckBadgeIcon className="w-5 h-5 text-blue/90" />} // Use CheckBadgeIcon
                                        iconBg="bg-blue/10"
                                    />
                                    <StatCard
                                        title={t('Completed')}
                                        value={dashboardData?.completed_chartered_boat_bookings || 0}
                                        icon={<CheckCircleIcon className="w-5 h-5 text-green/90" />} // Use CheckCircleIcon
                                        iconBg="bg-green/10"
                                    />
                                    <StatCard
                                        title={t('Cancelled by User')}
                                        value={dashboardData?.cancelled_chartered_boat_bookings || 0}
                                        icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                        iconBg="bg-[#fae6eb]"
                                    />
                                    <StatCard
                                        title={t('Trip Cancelled')}
                                        value={dashboardData?.trip_cancelled_chartered_boat_bookings || 0}
                                        icon={<ExclamationTriangleIcon className="w-5 h-5 text-orange-400" />}
                                        iconBg="bg-orange-100"
                                    />
                                    {/* <StatCard
                                        title={t('Total Passenger')}
                                        value={dashboardData?.total_passengers || 0}
                                        icon={<UsersIcon className="w-5 h-5 text-primary/90" />}
                                        iconBg="bg-primary/10"
                                    /> */}
                                </div>
                            </div>
                        </div>

                        <div className="mb-8 flex items-center gap-6">
                            <div className="w-[49%] relative">
                                <div className="bg-white rounded-xl w-full shadow border border-gray-100 p-4 pt-8">
                                    <div className="absolute -top-3 left-4 z-10">
                                        <span className="px-3 py-1 text-gray-800 bg-white text-md font-bold">
                                            {t('Total Passengers Travelled (Charter Boat)')}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4 print:grid-cols-2 print:gap-2 print:mb-2">
                                        <StatCard
                                            title={t('Passengers Transported')}
                                            value={dashboardData?.charter_boat_passengers_completed || 0}
                                            icon={<img src={stSeat} className="w-5 h-5" alt="Standard" />}
                                            iconBg="bg-primary/10"
                                        />
                                        <StatCard
                                            title={t('Passengers Cancelled')}
                                            value={dashboardData?.charter_boat_passengers_cancelled || 0}
                                            icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                            iconBg="bg-[#fae6eb]"
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* <div className="w-full relative">
                                <div className="bg-white rounded-xl w-full shadow border border-gray-100 p-4 pt-8">
                                    <div className="absolute -top-3 left-4 z-10">
                                        <span className="px-3 py-1 text-gray-800 bg-white text-md font-bold">
                                            {t('Total Passenger Traveled (Daypass Booking)')}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4 print:grid-cols-2 print:gap-2 print:mb-2">
                                        <StatCard
                                            title={t('Passengers Transported')}
                                            value={dashboardData?.daypass_passengers_completed || 0}
                                            icon={<TicketIcon className="w-5 h-5 text-primary" />}
                                            iconBg="bg-primary/10"
                                        />
                                        <StatCard
                                            title={t('Passengers Cancelled')}
                                            value={dashboardData?.daypass_passengers_cancelled || 0}
                                            icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                            iconBg="bg-[#fae6eb]"
                                        />
                                    </div>
                                </div> */}
                            {/* </div> */}
                        </div>



                        {/* --- Ticket-Class Distribution, Fleet Availability, Passenger Demographics on same row --- */}
                        <div className="flex flex-col lg:flex-row gap-4 print:flex-col print:gap-2">
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[35%] print:w-full">
                                <h2 className="text-lg font-semibold mb-4">{t('Ticket-Class Distribution')}</h2>
                                {!dashboardData?.ticket_class_distribution?.length ? (
                                    <GraphQlResponseNoData className="!py-10 !mt-0" />
                                ) : (
                                    <div className="chartClassDistribution">
                                        <div className="overflow-x-auto print:overflow-visible print:min-w-0 print:w-full">
                                            <div className="min-w-[800px] lg:min-w-full print:min-w-0 print:w-full">
                                                <div ref={chartRef} style={{ height: '200px' }}></div>
                                            </div>
                                        </div>
                                        <div className="statsSection">
                                            <div className="flex justify-between px-4">
                                                <div className="flex items-center gap-x-2">
                                                    <div className="bg-[#d4b242]/20 rounded-lg p-2">
                                                        <img src={vipSeat} alt="VIP Seat" className="w-4 h-4" />
                                                    </div>
                                                    <p className="font-semibold font-md">{t('VIP')}</p>
                                                </div>
                                                <p className="text-ms font-extrabold text-[#d4b242]">
                                                    {dashboardData.ticket_class_distribution.reduce(
                                                        (acc, item) => acc + item.vip_seat_count,
                                                        0
                                                    )}
                                                </p>
                                            </div>
                                            <div className="flex justify-between mt-4 px-4">
                                                <div className="flex items-center gap-x-2">
                                                    <div className="bg-[#6fa0ad]/20 rounded-lg p-2">
                                                        <img src={stSeat} alt="VIP Seat" className="w-4 h-4" />
                                                    </div>
                                                    <p className="font-semibold font-md">{t('Standard')}</p>
                                                </div>
                                                <p className="text-ms font-extrabold text-[#6fa0ad]">
                                                    {dashboardData.ticket_class_distribution.reduce(
                                                        (acc, item) => acc + item.std_seat_count,
                                                        0
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[30%] print:w-full">
                                <h2 className="text-lg font-semibold mb-4">{t('Fleet Availability')}</h2>
                                {dashboardData?.fleet_availability?.map((item, index) => (
                                    <ProgressBar
                                        key={index}
                                        label={item.boat_status[i18next.language || 'en']}
                                        percentage={item.avl_percentage}
                                    />
                                ))}
                                <p className="text-md text-primary font-semibold mt-8">
                                    {t('Total Boats number')}:{' '}
                                    {dashboardData?.fleet_availability?.[0]?.total_count || 0}
                                </p>
                            </div>

                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[35%] print:w-full">
                                <h2 className="text-lg font-semibold mb-6">{t('Passenger Demographics')}</h2>
                                <div className="space-y-4">
                                    {DEMOGRAPHICS_CONFIG.map((demo, index) => (
                                        <StatProgressBar
                                            key={index}
                                            label={demo.label}
                                            percentage={dashboardData?.passenger_demographics?.[demo.key] || 0}
                                            range={demo.range}
                                            iconBg={demo.iconBg}
                                            iconColor={demo.iconColor}
                                            Icon={demo.Icon}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="bgwhite p-4 mt-6 rounded-xl shadow-lg border border-gray-100 w-full mb-6 print:w-full">
                            <h2 className="text-lg font-semibold mb-4">{t('Hourly Booking Trends')}</h2>
                            <div className="overflow-x-auto">
                                <div className="min-w-[1200px] lg:min-w-full">
                                    <div ref={hourlyChartRef} style={{ height: '320px' }}></div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col lg:flex-row gap-4 mt-6 print:flex-col print:gap-2 print:mt-2">
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[45%] print:w-full">
                                <h2 className="text-lg font-semibold mb-6">
                                    {t('Most Popular Routes (by Passengers)')}
                                </h2>
                                <div className="overflow-y-auto pr-2">
                                    <div className="space-y-4">
                                        {dashboardData?.top_routes_by_bookings?.map((route, index) => (
                                            <RouteProgress
                                                key={index}
                                                route={route.route_name[i18next.language || 'en']}
                                                value={route.pass_count || 0}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[55%] overflow-x-auto print:w-full">
                                <h2 className="text-lg font-semibold mb-6">
                                    {t('Check-in/Check-out Trends by Station')}
                                </h2>
                                <div className="overflow-y-auto">
                                    <div className="min-w-[600px] lg:min-w-full">
                                        {' '}
                                        {/* Add minimum width for table scroll */}
                                        <table className="min-w-full">
                                            <thead>
                                                <tr className="border-b border-gray-200">
                                                    <th className="text-left py-2 text-gray-400 text-sm font-medium">
                                                        {t('Route')}
                                                    </th>
                                                    <th className="text-center py-2 text-gray-400 text-sm font-medium">
                                                        {t('checked in')}
                                                    </th>
                                                    <th className="text-center py-2 text-gray-400 text-sm font-medium">
                                                        {t('checked out')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {dashboardData?.check_in_out_trends?.map((trip, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="py-3 text-sm text-primary font-semibold max-w-[200px] truncate">
                                                            {trip.station_name[i18next.language || 'en']}
                                                        </td>
                                                        <td className="py-3 text-sm text-primary font-semibold text-center">
                                                            {trip.checkin_count}
                                                        </td>
                                                        <td className="py-3 text-sm text-primary font-semibold text-center">
                                                            {trip.checkout_count}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default Dashboard
